import type { TurnJudgement } from "../motion/turn"
import type { PhoneCheck, PlannedShot } from "../types"
import { judgeShot } from "./combine"
import { topView } from "./head"
import { loadVision } from "./vision-client"
import type { ViewReading } from "./vision-engine"

// The phone's look at one photo, for the step it was taken for. Always
// settles: a brain that can't load (or a slow first load) gives null after
// CHECK_TIMEOUT_MS, and the walk carries on with the server's check alone.

export const CHECK_TIMEOUT_MS = 6000

export function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([p, new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))])
}

export async function readPhoto(photo: Blob): Promise<ViewReading | null> {
  const vision = await loadVision()
  return vision?.caps.brain ? vision.photo(photo) : null
}

export async function checkPhoto(
  photo: Blob,
  shot: PlannedShot | null,
  turn: TurnJudgement | null,
): Promise<PhoneCheck | null> {
  // Steps the brain has nothing to say about.
  if (!shot || (shot.kind !== "wide" && shot.kind !== "closeup")) return null
  const reading = await withTimeout(readPhoto(photo).catch(() => null), CHECK_TIMEOUT_MS)
  const verdict = judgeShot({ shot, probs: reading?.probs ?? null, turn, taillights: reading?.taillights ?? null })
  if (!reading && verdict.kind === "unsure") return null
  const top = reading ? topView(reading.probs) : null
  return {
    verdict: verdict.kind,
    seen: verdict.kind === "wrong-corner" ? verdict.seen : null,
    view: top?.view ?? null,
    confidence: top?.p ?? 0,
  }
}
